/**
 * EnrollmentCard Component
 * Card para mostrar información de un enrollment pendiente
 */

import { formatDate } from '@/utils/dateUtils';
import { Card, CardContent, Typography, Box, Chip, Button, Stack } from '@mui/material';
import {
  Computer as ComputerIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import type { Enrollment } from '@/types/enrollment';

interface EnrollmentCardProps {
  enrollment: Enrollment;
  onApprove?: (enrollmentId: string) => void;
  onDelete?: (enrollmentId: string) => void;
  approving?: boolean;
  deleting?: boolean;
  /** 'pending' (default) muestra Aprobar/Rechazar; 'rejected' muestra "Volver a permitir". */
  variant?: 'pending' | 'rejected';
}

export const EnrollmentCard: React.FC<EnrollmentCardProps> = ({
  enrollment,
  onApprove,
  onDelete,
  approving = false,
  deleting = false,
  variant = 'pending',
}) => {
  const isRejected = variant === 'rejected';
  const hostname = enrollment.device?.hostname || '—';
  const os = enrollment.device?.os
    ? `${enrollment.device.os}${enrollment.device.arch ? ` ${enrollment.device.arch}` : ''}`
    : '—';
  const version = enrollment.device?.version || '—';
  const createdAt = enrollment.createdAt ? formatDate(enrollment.createdAt) : '—';

  return (
    <Card
      sx={{
        border: 1,
        borderColor: 'divider',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 2,
        },
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="flex-start" gap={2}>
          <ComputerIcon
            sx={{
              fontSize: 40,
              color: isRejected ? 'error.main' : 'warning.main',
            }}
          />

          <Box flex={1} minWidth={0}>
            <Typography variant="h6" component="div" noWrap sx={{ fontWeight: 600, mb: 0.5 }}>
              {enrollment.enrollmentId}
            </Typography>

            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary" noWrap>
                Hostname: {hostname}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                OS: {os}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                Versión: v{version}
              </Typography>
              {enrollment.pairingCode && (
                <Box>
                  <Chip
                    label={`Pairing: ${enrollment.pairingCode}`}
                    size="small"
                    color="info"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              )}
              <Typography variant="caption" color="text.secondary">
                Creado: {createdAt}
              </Typography>
            </Stack>
          </Box>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          {isRejected && enrollment.organizationId ? (
            <Typography variant="body2" color="text.secondary">
              Solicitud rechazada. Para agregar el equipo, usá un código nuevo en una instalación
              nueva.
            </Typography>
          ) : isRejected ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<ReplayIcon />}
              onClick={() => onApprove?.(enrollment.enrollmentId)}
              disabled={approving}
              fullWidth
            >
              {approving ? 'Permitiendo...' : 'Volver a permitir'}
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckIcon />}
                onClick={() => onApprove?.(enrollment.enrollmentId)}
                disabled={approving || deleting}
                fullWidth
              >
                {approving ? 'Aprobando...' : 'Aprobar'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => onDelete?.(enrollment.enrollmentId)}
                disabled={approving || deleting}
                fullWidth
              >
                {deleting ? 'Eliminando...' : 'Rechazar'}
              </Button>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
